/*
 *
 * choices
 * @author    Sam Rankin <srankin@riester.com>
 * @copyright 2023 RIESTER
 * @version   1.0.0
 */

import { isEmpty, allAttributes, extend, has, get, outputAttributes } from '../utils';

// eslint-disable-next-line
import { default as Choices } from 'choices.js';

import { default as classnames } from 'classnames';

/** @typedef {import("choices.js").Options} ChoiceOptions */

/**
 * Initialize a new choices.js instance
 *
 * @export
 * @param {HTMLElement} input
 * @param {ChoiceOptions} opts
 * @returns {Choices|false}
 */
export default function choice(input, opts) {
	if (!isEmpty(input)) {
		let placeholder = input.getAttribute('data-placeholder');

		let isSmall = input.classList.contains('form-select-sm');
		let isLarge = input.classList.contains('form-select-lg');

		if (isSmall) {
			input.classList.add('choices-sm');
		}

		if (isLarge) {
			input.classList.add('choices-lg');
		}

		let inputClasses = input.getAttribute('class');

		inputClasses = inputClasses.replace(/form-(?:control|select)/, '');

		/**
		 *
		 * @type ChoiceOptions
		 */
		let selectOptions = {
			searchEnabled: false,
			placeholderValue: placeholder,
			itemSelectText: '',
			shouldSort: false,
			shouldSortItems: false,
			removeItemButton: true,
			allowHTML: true,
			classNames: {
				containerOuter: 'choices ' + inputClasses,
			},

			callbackOnCreateTemplates: function (template) {
				return {
					item: ({ classNames }, data) => {
						let deleteBtn = '';
						if (this.config.removeItemButton) {
							const REMOVE_ITEM_TEXT = 'Remove item';

							deleteBtn = `<button data-button class="btn btn-link choices__button p-0" aria-label="${REMOVE_ITEM_TEXT}: ${data.value}"><span class="btn-text visually-hidden">${REMOVE_ITEM_TEXT}</span><span class="btn-icon icon-right"><i class="bi bi-x"></i></span></button>`;
						}

						let optionAttributes = {
							class: classnames({
								[classNames.item]: true,
								[classNames.placeholder]: data.placeholder,
								[classNames.highlightedState]: data.highlighted,
								[classNames.itemSelectable]: !data.highlighted,
							}),
							'data-item': '',
							'data-id': data.id,
							'data-value': data.value,
						};

						if (data.disabled) {
							optionAttributes['aria-disabled'] = 'true';
						}

						if (data.active) {
							optionAttributes['aria-selected'] = 'true';
						}

						return template(`<div ${outputAttributes(optionAttributes)}>${deleteBtn}<span class="option-text">${data.label.trim()}</span></div>`);
					},
					choice: ({ classNames }, data) => {
						// console.log('🚀 ~ file: choices.js:165 ~ choice ~ data:', data);
						let options = this.passedElement.element.options;

						let originalEl = options[data.id - 1];
						let optionAttributes = {
							class: classnames({
								[classNames.item]: true,
								[classNames.itemChoice]: true,
								[classNames.itemDisabled]: data.disabled,
								[classNames.itemSelectable]: !data.disabled,
								[classNames.selectedState]: data.selected,
								[classNames.placeholder]: data.placeholder,
								'd-none': !isEmpty(data.customProperties) && !data.customProperties.mobile,
								'd-md-none': !isEmpty(data.customProperties) && !data.customProperties.desktop,
								'd-block': !isEmpty(data.customProperties) && data.customProperties.mobile,
								'd-md-block': !isEmpty(data.customProperties) && data.customProperties.desktop,
							}),
							'data-choice': '',
							'data-id': data.id,
							'data-value': data.value,
						};
						if (data.groupId > 0) {
							optionAttributes['role'] = 'treeitem';
							optionAttributes['aria-disabled'] = 'true';
						} else {
							optionAttributes['role'] = 'option';
						}
						if (data.disabled) {
							optionAttributes['data-choice-disabled'] = '';
							optionAttributes['aria-disabled'] = 'true';
						} else {
							optionAttributes['data-choice-selectable'] = '';
						}

						// if (data.selected) {
						// 	optionAttributes['aria-selected'] = 'true';
						// }

						if (!isEmpty(originalEl)) {
							let optionClasses = optionAttributes.class;

							let originalAtts = allAttributes(originalEl);

							if (has(originalAtts, 'class')) {
								let originalClasses = get(originalAtts, 'class');

								optionClasses = classnames(optionClasses, originalClasses);
							}
							extend(optionAttributes, originalAtts);

							optionAttributes.class = optionClasses;
						}

						// console.log('🚀 ~ file: choices.js:154 ~ choice ~ optionAttributes:', optionAttributes);

						optionAttributes = outputAttributes(optionAttributes);

						let output = template(`<div ${optionAttributes}>${data.label.trim()}</div>`);

						// console.log('🚀 ~ file: choices.js:160 ~ choice ~ output:', output);

						// let outputData = output.dataset;
						// output.setAttribute('data-custom-properties', JSON.stringify(outputData));

						// console.log('🚀 ~ file: choices.js:144 ~ choice ~ output:', output);

						return output;
					},
					// option: (data) => {
					// 	console.log('🚀 ~ file: choices.js:191 ~ callbackOnCreateTemplates.option ~ data:', data);
					// 	let options = this.passedElement.element.options;

					// 	/**
					// 	 * @type {null|HTMLElement} opt
					// 	 */
					// 	let opt = options[data.id - 1];

					// 	let originalAtts = {};

					// 	if (isElement(opt)) {
					// 		let optData = data.customProperties;

					// 		// for (const key in opt.dataset) {
					// 		// 	if (Object.hasOwnProperty.call(opt.dataset, key)) {
					// 		// 		const value = opt.dataset[key];

					// 		// 		optData[key] = value;
					// 		// 	}
					// 		// }

					// 		console.log('🚀 ~ file: choices.js:177 ~ callbackOnCreateTemplates.option ~ optData:', optData);

					// 		// if (data.customProperties) {
					// 		// 	extend(optData, data.customProperties);
					// 		// 	// optData = `${JSON.stringify(optData)}`;
					// 		// }

					// 		originalAtts = allAttributes(opt);

					// 		originalAtts['data-custom-properties'] = optData;

					// 		// console.log('🚀 ~ file: choices.js:165 ~ choice ~ originalEl:', opt);
					// 	}

					// 	let optClasses = classnames(
					// 		{
					// 			'd-none': !data.customProperties.mobile,
					// 			'd-md-none': !data.customProperties.desktop,
					// 			'd-block': data.customProperties.mobile,
					// 			'd-md-block': data.customProperties.desktop,
					// 		},
					// 		originalAtts.class
					// 	);

					// 	originalAtts.class = optClasses;

					// 	return template(`<option ${outputAttributes(originalAtts)}>${data.label.trim()}</option>`);
					// },
				};
			},
		};

		extend(selectOptions, opts);

		return new Choices(input, selectOptions);
	} else {
		return false;
	}
}
